import Placeholder from '@tiptap/extension-placeholder';
import { type Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect } from 'react';
import { Markdown, type MarkdownStorage } from 'tiptap-markdown';

import { EmailEditorToolbar } from '@/components/EmailBodyEditor/EmailEditorToolbar.tsx';
import { cn } from '@/utils/cn.ts';

// tiptap-markdown does not augment TipTap's global Storage type, so read it explicitly.
const getMarkdown = (editor: Editor): string =>
	(
		editor.storage as unknown as { markdown: MarkdownStorage }
	).markdown.getMarkdown();

// Formatting classes for the editable content — mirrors the app's rendered-markdown
// convention (see AgentMessage.tsx) so lists get their markers back (Tailwind preflight
// resets ul/ol) and links/marks match the rest of the product.
const CONTENT_CLASS = cn(
	'min-h-full text-sm leading-relaxed text-(--text-primary) outline-none',
	'[&_p]:my-2 [&_strong]:font-semibold [&_em]:italic',
	'[&_a]:text-(--color-beacon-accent) [&_a]:underline',
	'[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-6',
	'[&_li]:leading-relaxed',
);

interface EmailBodyEditorProps {
	// The current body Markdown (fed from optimistic/server state).
	markdown: string;
	onCommit: (markdown: string) => void;
	placeholder?: string;
	className?: string;
}

export const EmailBodyEditor = ({
	markdown,
	onCommit,
	placeholder = 'Write your email…',
	className,
}: EmailBodyEditorProps) => {
	const editor = useEditor({
		extensions: [
			StarterKit.configure({
				heading: false,
				blockquote: false,
				code: false,
				codeBlock: false,
				horizontalRule: false,
				link: {
					openOnClick: false,
					protocols: ['http', 'https', 'mailto'],
				},
			}),
			Markdown.configure({ html: false, transformPastedText: true }),
			Placeholder.configure({
				placeholder,
				emptyEditorClass: cn(
					'before:pointer-events-none before:float-left before:h-0',
					'before:text-(--text-disabled) before:content-[attr(data-placeholder)]',
				),
			}),
		],
		content: markdown,
		editorProps: {
			attributes: { class: CONTENT_CLASS },
		},
		onBlur: ({ editor: e }) => {
			onCommit(getMarkdown(e));
		},
	});

	// Controlled update: reflect server/agent-driven body changes while the panel is
	// open, but never clobber the user mid-edit. Mirrors TiptapEditor.tsx's controlled
	// pattern (only when the editor is not focused and the value actually differs).
	useEffect(() => {
		if (!editor) return;
		if (editor.isFocused) return;
		const current = getMarkdown(editor);
		if (markdown !== current) {
			editor.commands.setContent(markdown);
		}
	}, [editor, markdown]);

	if (!editor) return null;

	return (
		<div className={cn('flex min-h-0 flex-1 flex-col', className)}>
			<EmailEditorToolbar editor={editor} className="px-3 py-2" />
			<div className="scrollbar-hidden min-h-0 flex-1 overflow-y-auto px-3 py-3">
				<EditorContent editor={editor} className="min-h-full" />
			</div>
		</div>
	);
};
